import { useId } from 'react';

import { WEB_NETWORK_GRAPH } from '../../network/generated-network-graph';

export type RouteCandidatePickerSource = 'history' | 'network' | 'template';

export type RouteCandidatePickerNetworkAvailability =
  | { readonly status: 'loading' }
  | {
      readonly reason:
        'corrupt' | 'graph_not_bundled' | 'incompatible' | 'not_prepared' | 'not_release_eligible';
      readonly status: 'unavailable';
    }
  | { readonly status: 'ready_no_route' }
  | { readonly status: 'ready' };

export interface RouteCandidatePickerItem {
  readonly autoPreselected: boolean;
  readonly confirmed: boolean;
  readonly destinationName: string;
  readonly distanceMetres: number | null;
  readonly distanceQuality: 'confirmed' | 'estimated' | 'unknown';
  readonly explicitConfirmationCount: number;
  readonly favorite: boolean;
  readonly favoriteMutable: boolean;
  readonly group: 'history' | 'network' | 'templates';
  readonly id: string;
  readonly lastUsedServiceDate: string | null;
  readonly name: string | null;
  readonly originName: string;
  readonly rank: number;
  readonly scorePoints: number;
  readonly routeVariantId: string | null;
  readonly selectionMethod: 'automatic' | 'explicit' | null;
  readonly source: RouteCandidatePickerSource;
  readonly useCount: number;
  readonly viaNames: readonly string[];
}

export function RouteCandidatePicker({
  candidates,
  disabled,
  favoriteBusyId,
  manualConfirmed,
  manualSelected,
  networkAvailability,
  onSelect,
  onSelectManual,
  onToggleFavorite,
  selectedCandidateId,
}: {
  readonly candidates: readonly RouteCandidatePickerItem[];
  readonly disabled: boolean;
  readonly favoriteBusyId: string | null;
  readonly manualConfirmed: boolean;
  readonly manualSelected: boolean;
  readonly networkAvailability: RouteCandidatePickerNetworkAvailability;
  readonly onSelect: (candidateId: string) => void;
  readonly onSelectManual: () => void;
  readonly onToggleFavorite: (candidate: RouteCandidatePickerItem) => void;
  readonly selectedCandidateId: string | null;
}) {
  const groupName = useId();
  const history = candidates.filter((candidate) => candidate.group === 'history');
  const templates = candidates.filter((candidate) => candidate.group === 'templates');
  const network = candidates.filter((candidate) => candidate.group === 'network');

  return (
    <fieldset className="route-candidate-picker">
      <legend>Kudy jsi jel?</legend>
      <p className="field-description">
        Vyber skutečně projetou variantu. Výběr ani automatická předvolba nejsou potvrzením trasy;
        potvrzení zůstává samostatný krok.
      </p>

      <CandidateGroup
        candidates={history}
        disabled={disabled}
        emptyMessage="Pro tento směr zatím není žádná dříve potvrzená varianta."
        favoriteBusyId={favoriteBusyId}
        groupName={groupName}
        heading="Dříve potvrzené"
        onSelect={onSelect}
        onToggleFavorite={onToggleFavorite}
        selectedCandidateId={selectedCandidateId}
      />

      <CandidateGroup
        candidates={templates}
        disabled={disabled}
        emptyMessage="Pro tento směr zatím není žádná použitelná šablona ani oblíbená varianta."
        favoriteBusyId={favoriteBusyId}
        groupName={groupName}
        heading="Šablony a oblíbené"
        onSelect={onSelect}
        onToggleFavorite={onToggleFavorite}
        selectedCandidateId={selectedCandidateId}
      />

      {networkAvailability.status === 'ready' ? (
        <>
          <CandidateGroup
            candidates={network}
            disabled={disabled}
            emptyMessage="Síťový graf pro tento směr nevrátil žádnou použitelnou variantu."
            favoriteBusyId={favoriteBusyId}
            groupName={groupName}
            heading="Síťové návrhy"
            onSelect={onSelect}
            onToggleFavorite={onToggleFavorite}
            selectedCandidateId={selectedCandidateId}
          />
          <NetworkDataNotice />
        </>
      ) : (
        <section className="route-candidate-group" aria-label="Síťové návrhy">
          <h3>Síťové návrhy</h3>
          <p className="graph-unavailable" role="status">
            {networkAvailability.status === 'loading'
              ? 'Připravuji místní síťový graf…'
              : networkAvailability.status === 'ready_no_route'
                ? 'Síťový návrh pro tento směr není dostupný: alespoň jedno místo není napojené na graf nebo mezi nimi nebyla nalezena cesta.'
                : networkUnavailableLabel(networkAvailability.reason)}{' '}
            Ruční, historické a šablonové trasy zůstávají dostupné; aplikace nic neodhaduje vzdušnou
            čarou.
          </p>
          <NetworkDataNotice />
        </section>
      )}

      <section className="route-candidate-group" aria-labelledby={`${groupName}-manual-heading`}>
        <h3 id={`${groupName}-manual-heading`}>Jiná trasa</h3>
        <label
          className={
            manualSelected ? 'route-candidate-option is-selected' : 'route-candidate-option'
          }
        >
          <input
            checked={manualSelected}
            disabled={disabled}
            name={groupName}
            type="radio"
            onChange={onSelectManual}
          />
          <span className="route-candidate-content">
            <strong>Sestavit jinou trasu ručně</strong>
            <span>Body Přes lze přidat, odebrat a seřadit bez síťového grafu.</span>
            <span className="route-candidate-badges">
              {manualSelected ? <Badge>vybraná varianta</Badge> : null}
              {manualConfirmed ? <Badge>výslovně potvrzeno</Badge> : null}
            </span>
          </span>
        </label>
      </section>
    </fieldset>
  );
}

function CandidateGroup({
  candidates,
  disabled,
  emptyMessage,
  favoriteBusyId,
  groupName,
  heading,
  onSelect,
  onToggleFavorite,
  selectedCandidateId,
}: {
  readonly candidates: readonly RouteCandidatePickerItem[];
  readonly disabled: boolean;
  readonly emptyMessage: string;
  readonly favoriteBusyId: string | null;
  readonly groupName: string;
  readonly heading: string;
  readonly onSelect: (candidateId: string) => void;
  readonly onToggleFavorite: (candidate: RouteCandidatePickerItem) => void;
  readonly selectedCandidateId: string | null;
}) {
  const headingId = `${groupName}-${
    heading === 'Dříve potvrzené'
      ? 'history'
      : heading === 'Síťové návrhy'
        ? 'network'
        : 'templates'
  }`;
  return (
    <section className="route-candidate-group" aria-labelledby={headingId}>
      <h3 id={headingId}>{heading}</h3>
      {candidates.length === 0 ? <p>{emptyMessage}</p> : null}
      {candidates.length > 0 ? (
        <ul className="route-candidate-list">
          {candidates.map((candidate) => {
            const selected = candidate.id === selectedCandidateId;
            return (
              <li className="route-candidate-item" key={candidate.id}>
                <label
                  className={
                    selected ? 'route-candidate-option is-selected' : 'route-candidate-option'
                  }
                >
                  <input
                    checked={selected}
                    disabled={disabled}
                    name={groupName}
                    type="radio"
                    onClick={() => {
                      if (selected && candidate.selectionMethod === 'automatic') {
                        onSelect(candidate.id);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (
                        selected &&
                        candidate.selectionMethod === 'automatic' &&
                        (event.key === ' ' || event.key === 'Enter')
                      ) {
                        event.preventDefault();
                        onSelect(candidate.id);
                      }
                    }}
                    onChange={() => onSelect(candidate.id)}
                  />
                  <span className="route-candidate-content">
                    <strong>{candidate.name ?? routePath(candidate)}</strong>
                    {candidate.name === null ? null : <span>{routePath(candidate)}</span>}
                    <span>{distanceLabel(candidate)}</span>
                    <span>
                      potvrzeno {candidate.explicitConfirmationCount}× · použito{' '}
                      {candidate.useCount}× ·{' '}
                      {lastUsedLabel(candidate.lastUsedServiceDate, candidate.useCount)}
                    </span>
                    <span className="route-candidate-badges">
                      <Badge>
                        {
                          {
                            history: 'dříve použito',
                            network: 'návrh sítě',
                            template: 'uložená šablona',
                          }[candidate.source]
                        }
                      </Badge>
                      {candidate.favorite ? <Badge>oblíbená</Badge> : null}
                      {candidate.autoPreselected ? <Badge>předvolená podle historie</Badge> : null}
                      {selected ? <Badge>vybraná varianta</Badge> : null}
                      {candidate.selectionMethod === 'automatic' ? (
                        <Badge>automaticky vybraná</Badge>
                      ) : null}
                      {candidate.selectionMethod === 'explicit' ? (
                        <Badge>výslovně vybraná</Badge>
                      ) : null}
                      {candidate.confirmed ? <Badge>výslovně potvrzeno</Badge> : null}
                    </span>
                  </span>
                </label>
                {!candidate.favoriteMutable ? null : (
                  <button
                    type="button"
                    className="secondary-action touch-target route-favorite-action"
                    disabled={disabled || favoriteBusyId !== null}
                    aria-pressed={candidate.favorite}
                    onClick={() => onToggleFavorite(candidate)}
                  >
                    {favoriteBusyId === candidate.id
                      ? `Ukládám oblíbenou variantu: ${candidate.name ?? routePath(candidate)}…`
                      : candidate.favorite
                        ? `Odebrat z oblíbených: ${candidate.name ?? routePath(candidate)}`
                        : `Přidat do oblíbených: ${candidate.name ?? routePath(candidate)}`}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

function Badge({ children }: { readonly children: string }) {
  return <span className="route-candidate-badge">{children}</span>;
}

function NetworkDataNotice() {
  return (
    <div className="network-data-notice">
      <p>Pokrytí: {WEB_NETWORK_GRAPH.coverageLabel}.</p>
      <p>
        Pro propojenou dvojici se nabízí jedna nejkratší varianta podle délek hran; další
        alternativy toto vydání neřadí.
      </p>
      <p>
        Návrh používá statickou železniční topologii; není navigací ani potvrzením aktuální
        průjezdnosti.
      </p>
      <ul className="network-data-links" aria-label="Licence a původ síťového grafu">
        <li>
          <a
            className="touch-target"
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noreferrer"
          >
            Data © OpenStreetMap contributors
          </a>
        </li>
        <li>
          <a
            className="touch-target"
            href={WEB_NETWORK_GRAPH.licenseUrl}
            target="_blank"
            rel="noreferrer"
          >
            ODbL 1.0
          </a>
        </li>
        <li>
          <a
            className="touch-target"
            href={WEB_NETWORK_GRAPH.readmeUrl}
            target="_blank"
            rel="noreferrer"
          >
            Původ a úpravy grafu
          </a>
        </li>
      </ul>
    </div>
  );
}

function routePath(candidate: RouteCandidatePickerItem): string {
  return [candidate.originName, ...candidate.viaNames, candidate.destinationName].join(' → ');
}

function distanceLabel(candidate: RouteCandidatePickerItem): string {
  if (candidate.distanceMetres === null) return 'kilometráž neznámá';
  const distance = (candidate.distanceMetres / 1_000).toLocaleString('cs-CZ', {
    maximumFractionDigits: 3,
  });
  const quality = {
    confirmed: 'potvrzená kilometráž',
    estimated: 'odhadovaná kilometráž',
    unknown: 'kilometráž s neznámou kvalitou',
  }[candidate.distanceQuality];
  return `${distance} km · ${quality}`;
}

function lastUsedLabel(value: string | null, useCount: number): string {
  if (value === null) {
    return useCount === 0 ? 'dosud nepoužito' : 'použito, datum služby neuvedeno';
  }
  const timestamp = Date.parse(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(timestamp)) return 'datum posledního použití neznámé';
  return `naposledy ve službě ${new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium', timeZone: 'UTC' }).format(timestamp)}`;
}

function networkUnavailableLabel(
  reason: Extract<RouteCandidatePickerNetworkAvailability, { status: 'unavailable' }>['reason'],
): string {
  switch (reason) {
    case 'graph_not_bundled':
      return 'Síťový návrh není v tomto vydání dostupný.';
    case 'not_release_eligible':
      return 'Síťový graf nemá povolení pro toto vydání.';
    case 'not_prepared':
      return 'Síťový graf nebyl v tomto zařízení připraven a nyní jej nelze načíst.';
    case 'incompatible':
      return 'Dostupný síťový graf není kompatibilní s tímto vydáním.';
    case 'corrupt':
      return 'Síťový graf neprošel kontrolou integrity a nebyl použit.';
  }
}
